import {
  AppleWalletBackField,
  GoogleWalletBackField,
  GoogleWalletBackLink,
} from '../../../services/queries/useBulkCreatePasses'
import { CsvRow } from './csvUtils'

interface ParsedBackFields {
  appleWalletBackFields: AppleWalletBackField[]
  googleWalletBackFields: GoogleWalletBackField[]
  googleWalletBackLinks: GoogleWalletBackLink[]
}

/**
 * Parses back fields from a CSV row
 * Supports AppleWalletBackFields, GoogleWalletBackFields, and GoogleWalletBackLinks
 */
export const parseBackFieldsFromRow = (row: CsvRow): ParsedBackFields => {
  const appleWalletBackFields: AppleWalletBackField[] = []
  const googleWalletBackFields: GoogleWalletBackField[] = []
  const googleWalletBackLinks: GoogleWalletBackLink[] = []

  // Parse AppleWalletBackFields (e.g., AppleWalletBackFields1.Label, AppleWalletBackFields1.Value)
  // Also support typo: AppleWalletBackField1.Value (missing 's')
  const appleFieldPattern = /^AppleWalletBackFields?(\d+)\.(Label|Value)$/i
  const appleFieldsMap = new Map<number, { label?: string; value?: string }>()

  Object.keys(row).forEach(key => {
    const match = key.match(appleFieldPattern)
    if (match) {
      const sortOrder = parseInt(match[1], 10) - 1 // Convert 1-based to 0-based
      const fieldType = match[2].toLowerCase()
      const value = row[key]

      if (!appleFieldsMap.has(sortOrder)) {
        appleFieldsMap.set(sortOrder, {})
      }

      const field = appleFieldsMap.get(sortOrder)
      if (field) {
        if (fieldType === 'label') {
          field.label = value || ''
        } else if (fieldType === 'value') {
          field.value = value || ''
        }
      }
    }
  })

  // Convert map to array
  Array.from(appleFieldsMap.entries())
    .sort(([a], [b]) => a - b)
    .forEach(([sortOrder, field]) => {
      if (field.label || field.value) {
        appleWalletBackFields.push({
          label: field.label || '',
          value: field.value || '',
          sortOrder: sortOrder + 1, // API expects 1-based sortOrder
        })
      }
    })

  // Parse GoogleWalletBackFields (e.g., GoogleWalletBackFields1.Label, GoogleWalletBackFields1.Value, GoogleWalletBackFields1.Field)
  // Support "Field" as an alias for "Label"
  const googleFieldPattern = /^GoogleWalletBackFields(\d+)\.(Label|Value|Field)$/i
  const googleFieldsMap = new Map<number, { label?: string; value?: string }>()

  Object.keys(row).forEach(key => {
    const match = key.match(googleFieldPattern)
    if (match) {
      const sortOrder = parseInt(match[1], 10) - 1 // Convert 1-based to 0-based
      const fieldType = match[2].toLowerCase()
      const value = row[key]

      if (!googleFieldsMap.has(sortOrder)) {
        googleFieldsMap.set(sortOrder, {})
      }

      const field = googleFieldsMap.get(sortOrder)
      if (field) {
        if (fieldType === 'label' || fieldType === 'field') {
          field.label = value || ''
        } else if (fieldType === 'value') {
          field.value = value || ''
        }
      }
    }
  })

  // Convert map to array
  Array.from(googleFieldsMap.entries())
    .sort(([a], [b]) => a - b)
    .forEach(([sortOrder, field]) => {
      if (field.label || field.value) {
        googleWalletBackFields.push({
          label: field.label || '',
          value: field.value || '',
          sortOrder: sortOrder + 1, // API expects 1-based sortOrder
        })
      }
    })

  // Parse GoogleWalletBackLinks (e.g., GoogleWalletBackLinks1.Description, GoogleWalletBackLinks1.URIValue, GoogleWalletBackLinks1.Value)
  // Support both "URIValue" and "Value" for the URL field
  const linkPattern = /^GoogleWalletBackLinks(\d+)\.(Description|URIValue|Value)$/i
  const linksMap = new Map<number, { description?: string; uriValue?: string }>()

  Object.keys(row).forEach(key => {
    const match = key.match(linkPattern)
    if (match) {
      const sortOrder = parseInt(match[1], 10) - 1 // Convert 1-based to 0-based
      const fieldType = match[2].toLowerCase()
      const value = row[key]

      if (!linksMap.has(sortOrder)) {
        linksMap.set(sortOrder, {})
      }

      const link = linksMap.get(sortOrder)
      if (link) {
        if (fieldType === 'description') {
          link.description = value || ''
        } else if (fieldType === 'urivalue' || fieldType === 'value') {
          link.uriValue = value || ''
        }
      }
    }
  })

  // Convert map to array
  Array.from(linksMap.entries())
    .sort(([a], [b]) => a - b)
    .forEach(([sortOrder, link]) => {
      if (link.description || link.uriValue) {
        googleWalletBackLinks.push({
          description: link.description || '',
          uriValue: link.uriValue || '',
          sortOrder: sortOrder + 1, // API expects 1-based sortOrder
        })
      }
    })

  return {
    appleWalletBackFields,
    googleWalletBackFields,
    googleWalletBackLinks,
  }
}
